<script setup lang="ts">
const props = defineProps<{
  email: string
  // Code exige apres un mot de passe valide, l'appareil n'etant pas reconnu
  deviceCheck?: boolean
}>()

const emit = defineEmits<{
  success: []
  resend: []
  passkeyRequired: []
}>()

const store = useAuthStore()
const toast = useToast()

const code = ref<number[]>([])
const loading = ref(false)
const resending = ref(false)

// Delai avant de pouvoir renvoyer un code : evite les envois en rafale
const RESEND_DELAY = 30
const countdown = ref(RESEND_DELAY)
let timer: ReturnType<typeof setInterval> | undefined

function startCountdown() {
  countdown.value = RESEND_DELAY
  clearInterval(timer)
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) clearInterval(timer)
  }, 1000)
}

onMounted(startCountdown)
onBeforeUnmount(() => clearInterval(timer))

// `@complete` ne se declenche qu'une fois les six cases remplies : aucune n'est vide
function onPinComplete(value: (number | undefined)[]) {
  code.value = value.filter((v): v is number => v !== undefined)
  handleSubmit()
}

async function handleSubmit() {
  const codeStr = code.value.join('')
  if (!codeStr || codeStr.length < 6) return

  loading.value = true
  try {
    const step = await store.verifyOtp(props.email, codeStr)
    if (step.passkeyRequired) {
      emit('passkeyRequired')
    }
    else {
      emit('success')
    }
  }
  catch (err) {
    const message = (err as { data?: { message?: string } }).data?.message
    toast.add({
      title: 'Code invalide',
      description: message || 'Le code est incorrect ou a expiré',
      color: 'error',
    })
    code.value = []
  }
  finally {
    loading.value = false
  }
}

async function resend() {
  resending.value = true
  try {
    await store.sendEmailCode(props.email)
    toast.add({
      title: 'Code renvoyé',
      description: 'Un nouveau code a été envoyé',
      color: 'success',
    })
    emit('resend')
    startCountdown()
  }
  catch {
    toast.add({
      title: 'Erreur',
      description: 'Impossible de renvoyer le code',
      color: 'error',
    })
  }
  finally {
    resending.value = false
  }
}
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleSubmit">
    <UAlert
      v-if="deviceCheck"
      color="info"
      variant="subtle"
      icon="i-lucide-shield-alert"
      title="Appareil non reconnu"
      description="Par sécurité, confirmez votre connexion avec le code reçu par email. Cet appareil sera ensuite reconnu pendant trois mois."
    />

    <p class="text-sm text-center text-[var(--ui-text-muted)]">
      Entrez le code à 6 chiffres envoyé à <strong>{{ email }}</strong>
    </p>

    <div class="flex justify-center">
      <UPinInput
        v-model="code"
        :length="6"
        otp
        type="number"
        size="xl"
        placeholder="○"
        :ui="{ base: 'size-12 text-xl font-semibold' }"
        autofocus
        @complete="onPinComplete"
      />
    </div>

    <UButton
      type="submit"
      block
      size="lg"
      color="secondary"
      :loading="loading"
      :disabled="code.join('').length < 6"
    >
      Vérifier
    </UButton>

    <div class="text-center">
      <UButton
        variant="link"
        size="sm"
        :loading="resending"
        :disabled="countdown > 0"
        @click="resend"
      >
        {{ countdown > 0 ? `Renvoyer un code dans ${countdown} s` : 'Renvoyer un code' }}
      </UButton>
    </div>
  </form>
</template>
